import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Apple, Laptop, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Skeleton, Switch } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

interface AppsOverview {
  apns: { teamId: string; keyId: string; bundleId: string; production: boolean; updatedAt: string } | null;
  windows: { version: string; size: number; sha256: string; uploadedAt: string } | null;
}

const KEY = ['platform', 'apps'];

/** Plattformverwaltung → Apps: Windows-Installer bereitstellen und Apple-Push (APNs) für die iPhone/iPad-App einrichten. */
export function AppsPage() {
  const { data, isLoading } = useQuery({ queryKey: KEY, queryFn: () => api<AppsOverview>('/platform/apps') });
  return (
    <div className="grid max-w-3xl gap-6">
      <PageHeader title="Mitarbeiter-Apps" description="Gilt für alle Mandanten dieser Installation. Einrichtung auf den Geräten: Profil → App und Benachrichtigungen." />
      {isLoading || !data ? (
        <Skeleton className="h-64" />
      ) : (
        <>
          <WindowsCard data={data} />
          <ApnsCard data={data} />
        </>
      )}
    </div>
  );
}

function WindowsCard({ data }: { data: AppsOverview }) {
  const qc = useQueryClient();
  const [confirmDialog, confirm] = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);
  const [version, setVersion] = useState('');
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      if (version.trim()) form.append('version', version.trim());
      form.append('file', file);
      return api<AppsOverview>('/platform/apps/windows', { method: 'POST', form });
    },
    onSuccess: (d) => {
      qc.setQueryData(KEY, d);
      setVersion('');
      toast.success(`Windows-Programm ${d.windows?.version} bereitgestellt`);
    },
    onError: (e) => toast.error(errorMessage(e), { duration: 10_000 }),
  });
  const remove = useMutation({
    mutationFn: () => api<AppsOverview>('/platform/apps/windows', { method: 'DELETE' }),
    onSuccess: (d) => qc.setQueryData(KEY, d),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const w = data.windows;
  return (
    <Card>
      {confirmDialog}
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Laptop className="size-4" /> Windows-Programm (MSI)
        </CardTitle>
        <CardDescription>
          Installer aus <code>apps/desktop</code> (<code>npm run dist</code>) hier hochladen. Mitarbeitende laden ihn unter Profil → App und Benachrichtigungen herunter; das Programm verbindet sich danach mit ihrem Portal.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 text-sm">
        {w ? (
          <div className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2">
            <Badge variant="success">Version {w.version}</Badge>
            <span className="text-muted-foreground">
              {(w.size / 1024 / 1024).toFixed(1)} MB · hochgeladen {formatDateTime(w.uploadedAt)}
            </span>
            <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground" title={`SHA-256 ${w.sha256}`}>
              SHA-256 {w.sha256.slice(0, 16)}…
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="Installer entfernen"
              onClick={async () => {
                if (await confirm({ title: 'Installer entfernen?', description: 'Mitarbeitende können das Windows-Programm dann nicht mehr herunterladen. Installierte Programme laufen weiter.', confirmLabel: 'Entfernen', destructive: true })) remove.mutate();
              }}
            >
              <Trash2 />
            </Button>
          </div>
        ) : (
          <Alert>Noch kein Installer bereitgestellt.</Alert>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Version" hint="Leer lassen, wenn sie im Dateinamen steht (Serviceportal-Setup-1.0.0.msi)" className="w-56">
            <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="z. B. 1.0.0" />
          </Field>
          <input
            ref={fileRef}
            type="file"
            accept=".msi"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = '';
            }}
          />
          <Button onClick={() => fileRef.current?.click()} loading={upload.isPending}>
            <Upload /> {w ? 'Neue Version hochladen' : 'MSI hochladen'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ApnsCard({ data }: { data: AppsOverview }) {
  const qc = useQueryClient();
  const [confirmDialog, confirm] = useConfirm();
  const [form, setForm] = useState({ teamId: '', keyId: '', bundleId: '', production: true });
  const [key, setKey] = useState('');
  const [keyName, setKeyName] = useState('');
  useEffect(() => {
    if (data.apns) setForm({ teamId: data.apns.teamId, keyId: data.apns.keyId, bundleId: data.apns.bundleId, production: data.apns.production });
  }, [data.apns]);
  const save = useMutation({
    mutationFn: () => api<AppsOverview>('/platform/apps/apns', { method: 'PUT', body: { ...form, ...(key ? { key } : {}) } }),
    onSuccess: (d) => {
      qc.setQueryData(KEY, d);
      setKey('');
      setKeyName('');
      toast.success('Apple-Push gespeichert');
    },
    onError: (e) => toast.error(errorMessage(e), { duration: 10_000 }),
  });
  const remove = useMutation({
    mutationFn: () => api<AppsOverview>('/platform/apps/apns', { method: 'DELETE' }),
    onSuccess: (d) => qc.setQueryData(KEY, d),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: k === 'bundleId' ? e.target.value.trim() : e.target.value.trim().toUpperCase() }));

  return (
    <Card>
      {confirmDialog}
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Apple className="size-4" /> iPhone/iPad-App: Apple-Push (APNs)
          {data.apns ? <Badge variant="success">eingerichtet</Badge> : <Badge variant="outline">nicht eingerichtet</Badge>}
        </CardTitle>
        <CardDescription>
          Im Apple-Developer-Konto unter <em>Certificates, Identifiers &amp; Profiles → Keys</em> einen Schlüssel mit <em>Apple Push Notifications service (APNs)</em> anlegen und die .p8-Datei hier hochladen. Sie lässt sich bei
          Apple nur einmal herunterladen; sie wird verschlüsselt gespeichert und nie wieder angezeigt.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Team-ID" hint="Developer-Konto → Membership (10 Zeichen)">
            <Input value={form.teamId} onChange={set('teamId')} maxLength={10} required />
          </Field>
          <Field label="Schlüssel-ID (Key ID)" hint="Steht beim Schlüssel und im Dateinamen AuthKey_XXXXXXXXXX.p8">
            <Input value={form.keyId} onChange={set('keyId')} maxLength={10} required />
          </Field>
          <Field label="Bundle-ID der App" hint="Wie in Xcode, z. B. de.wolbeck.serviceportal" className="sm:col-span-2">
            <Input value={form.bundleId} onChange={set('bundleId')} required />
          </Field>
          <Field label="Schlüsseldatei (.p8)" hint={data.apns ? 'Leer lassen, um den gespeicherten Schlüssel zu behalten' : undefined} className="sm:col-span-2">
            <Input
              type="file"
              accept=".p8"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                setKey(f ? await f.text() : '');
                setKeyName(f?.name ?? '');
                const id = /AuthKey_([A-Z0-9]{10})\.p8$/.exec(f?.name ?? '')?.[1];
                if (id && !form.keyId) setForm((v) => ({ ...v, keyId: id }));
              }}
            />
            {keyName && <span className="text-xs text-muted-foreground">{keyName} ausgewählt</span>}
          </Field>
          <label className="flex items-start gap-3 sm:col-span-2">
            <Switch checked={form.production} onCheckedChange={(v) => setForm((f) => ({ ...f, production: v }))} />
            <span className="text-sm">
              <span className="font-medium">Produktion</span>
              <span className="block text-muted-foreground">An für TestFlight, App Store und Apple Business Manager. Aus nur für Apps, die direkt aus Xcode auf ein Gerät gespielt wurden (Entwicklung).</span>
            </span>
          </label>
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <Button type="submit" loading={save.isPending}>
              Speichern
            </Button>
            {data.apns && (
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  if (await confirm({ title: 'Apple-Push entfernen?', description: 'iPhones und iPads erhalten dann keine Benachrichtigungen mehr.', confirmLabel: 'Entfernen', destructive: true })) remove.mutate();
                }}
              >
                Entfernen
              </Button>
            )}
          </div>
          {data.apns && <p className="text-xs text-muted-foreground sm:col-span-2">Zuletzt gespeichert {formatDateTime(data.apns.updatedAt)}</p>}
        </form>
      </CardContent>
    </Card>
  );
}
